import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../App';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Badge } from '../components/ui/badge';
import { CalendarIcon, Check } from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AnimatedCheckmark = ({ show }) => (
  <AnimatePresence>
    {show && (
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0, opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50"
      >
        <div className="attendance-checkmark">
          <Check className="w-8 h-8 text-white" />
        </div>
      </motion.div>
    )}
  </AnimatePresence>
);

const AttendancePage = () => {
  const { user, sessionToken } = useAuth();
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [students, setStudents] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [showCheckmark, setShowCheckmark] = useState(false);
  
  // Report filters
  const [viewMode, setViewMode] = useState('daily'); // 'daily', 'monthly', 'range'
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [fromDate, setFromDate] = useState(1);
  const [toDate, setToDate] = useState(new Date().getDate());
  const [reportData, setReportData] = useState([]);

  const months = [
    { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
    { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
    { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
    { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' }
  ];
  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);
  const dates = Array.from({ length: 31 }, (_, i) => i + 1);

  useEffect(() => {
    fetchClasses();
  }, []);

  useEffect(() => {
    if (selectedClass) {
      fetchStudents();
      if (viewMode === 'daily') {
        fetchTodayAttendance();
      } else {
        fetchReportData();
      }
    }
  }, [selectedClass, selectedDate, viewMode, selectedMonth, selectedYear, fromDate, toDate]);

  const fetchClasses = async () => {
    try {
      const response = await axios.get(`${API}/classes`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setClasses(response.data);
      if (response.data.length > 0) {
        setSelectedClass(response.data[0]._id);
      }
    } catch (error) {
      console.error('Failed to fetch classes:', error);
      toast.error('Failed to load classes');
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await axios.get(`${API}/students?class_id=${selectedClass}`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setStudents(response.data);
    } catch (error) {
      console.error('Failed to fetch students:', error);
      toast.error('Failed to load students');
    }
  };

  const fetchTodayAttendance = async () => {
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const response = await axios.get(`${API}/attendance?class_id=${selectedClass}&date=${dateStr}`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });

      const attendanceMap = {};
      response.data.forEach(record => {
        attendanceMap[record.student_id] = record.status;
      });
      setAttendance(attendanceMap);
    } catch (error) {
      console.error('Failed to fetch attendance:', error);
    }
  };

  const fetchReportData = async () => {
    try {
      const response = await axios.get(
        `${API}/attendance/report?class_id=${selectedClass}&month=${selectedMonth}&year=${selectedYear}&from_date=${fromDate}&to_date=${toDate}&mode=${viewMode}`,
        { headers: { Authorization: `Bearer ${sessionToken}` } }
      );
      setReportData(response.data);
    } catch (error) {
      console.error('Failed to fetch report:', error);
    }
  };

  const markAttendance = async (studentId, status) => {
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      await axios.post(
        `${API}/attendance`,
        {
          student_id: studentId,
          class_id: selectedClass,
          date: dateStr,
          status
        },
        { headers: { Authorization: `Bearer ${sessionToken}` } }
      );

      setAttendance(prev => ({ ...prev, [studentId]: status }));
      
      if (status === 'present') {
        setShowCheckmark(true);
        setTimeout(() => setShowCheckmark(false), 800);
      }
      
      toast.success('Attendance marked!');
    } catch (error) {
      console.error('Failed to mark attendance:', error);
      toast.error('Failed to mark attendance');
    }
  };

  const markAllPresent = async () => {
    setLoading(true);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const attendances = students.map(student => ({
        student_id: student._id,
        class_id: selectedClass,
        date: dateStr,
        status: 'present'
      }));

      await axios.post(
        `${API}/attendance/bulk`,
        { attendances },
        { headers: { Authorization: `Bearer ${sessionToken}` } }
      );

      const newAttendance = {};
      students.forEach(student => {
        newAttendance[student._id] = 'present';
      });
      setAttendance(newAttendance);
      
      setShowCheckmark(true);
      setTimeout(() => setShowCheckmark(false), 800);
      
      toast.success('All students marked present!');
    } catch (error) {
      console.error('Failed to mark all present:', error);
      toast.error('Failed to mark all present');
    } finally {
      setLoading(false);
    }
  };

  const markAllAbsent = async () => {
    setLoading(true);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const attendances = students.map(student => ({
        student_id: student._id,
        class_id: selectedClass,
        date: dateStr,
        status: 'absent'
      }));

      await axios.post(
        `${API}/attendance/bulk`,
        { attendances },
        { headers: { Authorization: `Bearer ${sessionToken}` } }
      );

      const newAttendance = {};
      students.forEach(student => {
        newAttendance[student._id] = 'absent';
      });
      setAttendance(newAttendance);
      toast.success('All students marked absent!');
    } catch (error) {
      console.error('Failed to mark all absent:', error);
      toast.error('Failed to mark all absent');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'present':
        return 'bg-green-100 text-green-700 border-green-300';
      case 'absent':
        return 'bg-red-100 text-red-700 border-red-300';
      case 'late':
        return 'bg-blue-100 text-blue-700 border-blue-300';
      case 'homework':
        return 'bg-blue-100 text-blue-700 border-blue-300';
      default:
        return 'bg-slate-100 text-slate-700 border-slate-300';
    }
  };

  return (
    <Layout>
      <AnimatedCheckmark show={showCheckmark} />
      
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 font-heading mb-2" data-testid="attendance-title">
            Attendance
          </h1>
          <p className="text-slate-500 text-lg">Mark and view attendance reports</p>
        </div>

        {/* View Mode Tabs */}
        <div className="flex gap-2 mb-4">
          <Button
            onClick={() => setViewMode('daily')}
            className={`rounded-full ${viewMode === 'daily' ? 'bg-sky-500 text-white' : 'bg-slate-100 text-slate-700'}`}
          >
            Daily Mark
          </Button>
          <Button
            onClick={() => setViewMode('monthly')}
            className={`rounded-full ${viewMode === 'monthly' ? 'bg-sky-500 text-white' : 'bg-slate-100 text-slate-700'}`}
          >
            Monthly Report
          </Button>
          <Button
            onClick={() => setViewMode('range')}
            className={`rounded-full ${viewMode === 'range' ? 'bg-sky-500 text-white' : 'bg-slate-100 text-slate-700'}`}
          >
            Date Range
          </Button>
        </div>

        {/* Filters */}
        <Card className="p-6 rounded-2xl border-slate-100 bg-white mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Select Class</label>
              <Select value={selectedClass} onValueChange={setSelectedClass}>
                <SelectTrigger className="rounded-xl" data-testid="class-select">
                  <SelectValue placeholder="Select a class" />
                </SelectTrigger>
                <SelectContent>
                  {classes.map((cls) => (
                    <SelectItem key={cls._id} value={cls._id}>
                      {cls.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {viewMode === 'daily' && (
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">Select Date</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal rounded-xl">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(selectedDate, 'PPP')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={selectedDate} onSelect={setSelectedDate} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>
            )}

            {(viewMode === 'monthly' || viewMode === 'range') && (
              <>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">Month</label>
                  <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {months.map((m) => (
                        <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">Year</label>
                  <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {years.map((y) => (
                        <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            {viewMode === 'range' && (
              <>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">From Date</label>
                  <Select value={fromDate.toString()} onValueChange={(v) => setFromDate(parseInt(v))}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {dates.map((d) => (
                        <SelectItem key={d} value={d.toString()}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">To Date</label>
                  <Select value={toDate.toString()} onValueChange={(v) => setToDate(parseInt(v))}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {dates.map((d) => (
                        <SelectItem key={d} value={d.toString()}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
          </div>

          {viewMode === 'daily' && (
            <div className="flex gap-3 mt-4">
              <Button
                onClick={markAllPresent}
                disabled={loading || students.length === 0}
                className="bg-green-500 hover:bg-green-600 text-white rounded-full"
              >
                Mark All Present
              </Button>
              <Button
                onClick={markAllAbsent}
                disabled={loading || students.length === 0}
                variant="outline"
                className="rounded-full border-red-300 text-red-600 hover:bg-red-50"
              >
                Mark All Absent
              </Button>
            </div>
          )}
        </Card>

        {/* Daily Marking View */}
        {viewMode === 'daily' && (
          students.length === 0 ? (
            <Card className="p-8 rounded-2xl border-slate-100 bg-white text-center">
              <p className="text-slate-500">No students found in this class.</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {students.map((student) => (
                <Card key={student._id} className="p-4 rounded-2xl border-slate-100 bg-white hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center flex-1">
                      <div className="w-12 h-12 bg-sky-100 rounded-full flex items-center justify-center mr-4">
                        <span className="text-sky-600 font-semibold text-lg">{student.name.charAt(0)}</span>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-slate-900">{student.name}</h3>
                        <p className="text-sm text-slate-500">Roll No: {student.roll_no}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {['present', 'absent', 'late', 'homework'].map((status) => (
                        <button
                          key={status}
                          onClick={() => markAttendance(student._id, status)}
                          className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                            attendance[student._id] === status
                              ? status === 'present' ? 'bg-green-500 text-white' 
                                : status === 'absent' ? 'bg-red-500 text-white'
                                : 'bg-blue-500 text-white'
                              : status === 'present' ? 'bg-green-50 text-green-600 hover:bg-green-100'
                                : status === 'absent' ? 'bg-red-50 text-red-600 hover:bg-red-100'
                                : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                          }`}
                        >
                          {status.charAt(0).toUpperCase() + status.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )
        )}

        {/* Monthly/Range Report View */}
        {(viewMode === 'monthly' || viewMode === 'range') && (
          <Card className="rounded-2xl border-slate-100 bg-white overflow-hidden">
            <table className="w-full">
              <thead className="bg-sky-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Roll No</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Student Name</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Present</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Absent</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Late</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Homework Not Done</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Attendance %</th>
                </tr>
              </thead>
              <tbody>
                {reportData.length === 0 ? (
                  <tr><td colSpan="7" className="px-4 py-8 text-center text-slate-500">No attendance data for selected period</td></tr>
                ) : (
                  reportData.map((record, idx) => (
                    <tr key={idx} className="border-t border-slate-100">
                      <td className="px-4 py-3 text-sm">{record.roll_no}</td>
                      <td className="px-4 py-3 text-sm font-medium">{record.student_name}</td>
                      <td className="px-4 py-3 text-center"><span className="inline-block w-8 h-8 bg-green-100 text-green-700 rounded-full leading-8 text-sm font-semibold">{record.present}</span></td>
                      <td className="px-4 py-3 text-center"><span className="inline-block w-8 h-8 bg-red-100 text-red-700 rounded-full leading-8 text-sm font-semibold">{record.absent}</span></td>
                      <td className="px-4 py-3 text-center"><span className="inline-block w-8 h-8 bg-blue-100 text-blue-700 rounded-full leading-8 text-sm font-semibold">{record.late}</span></td>
                      <td className="px-4 py-3 text-center"><span className="inline-block w-8 h-8 bg-purple-100 text-purple-700 rounded-full leading-8 text-sm font-semibold">{record.homework}</span></td>
                      <td className="px-4 py-3 text-center font-semibold text-lg">{record.attendance_percentage}%</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default AttendancePage;
